import React, { useEffect, useState } from "react";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import Check from "@enerex/unified-ui-react/form/Check";
import FormInput from "@enerex/unified-ui-react/form/FormInput";
import LoadingButton from "@enerex/unified-ui-react/LoadingButton";
import Select from "@enerex/unified-ui-react/form/Select";
import { useForm } from "react-hook-form";
import "./EditStateOperationModal.css";

export const getSopEditSnapshot = (row) => {
  const mappings = row?.commodityMappings ?? row?.CommodityMappings;
  if (!Array.isArray(mappings)) {
    const id = row?.id ?? row?.Id;
    const commodityId = Number(row?.commodityId ?? row?.CommodityId);
    if (!/^[1-9]\d*$/.test(String(id)) || (typeof id === "number" && !Number.isSafeInteger(id)) ||
        (commodityId !== 1 && commodityId !== 2)) return null;
    return { expectedMappingIds: [String(id)], commodityIds: [commodityId] };
  }
  if (mappings.length !== 1) return null;
  const ids = mappings.map((mapping) => mapping.id ?? mapping.Id);
  const commodities = mappings.map((mapping) => Number(mapping.commodityId ?? mapping.CommodityId));
  if (ids.some((id) => !/^[1-9]\d*$/.test(String(id)) || (typeof id === "number" && !Number.isSafeInteger(id))) ||
      commodities.some((id) => id !== 1 && id !== 2) ||
      new Set(ids.map(String)).size !== ids.length || new Set(commodities).size !== commodities.length) return null;
  return { expectedMappingIds: ids.map(String), commodityIds: commodities };
};

const EditStateOperationModal = ({ row, stateLabel, saving, canRefresh = false, onRefresh, onHide, onSave }) => {
  const [error, setError] = useState("");
  const { control, getValues, reset } = useForm({
    defaultValues: {
      ServiceReferenceNumberRequired: false,
      ServiceReferenceNumberDescription: "",
      KeyNameRequired: false,
      KeyNameDescription: "",
      EnrollmentLeadTime: "",
    },
  });

  useEffect(() => {
    const snapshot = getSopEditSnapshot(row);
    setError(row && !snapshot ? "Mapping details are unavailable. Refresh the page before editing." : "");
    reset({
      ServiceReferenceNumberRequired: Boolean(row?.serviceReferenceNumberRequired ?? row?.ServiceReferenceNumberRequired),
      ServiceReferenceNumberDescription: row?.serviceReferenceNumberDescription ?? row?.ServiceReferenceNumberDescription ?? "",
      KeyNameRequired: Boolean(row?.keyNameRequired ?? row?.nameKeyRequired ?? row?.KeyNameRequired ?? row?.NameKeyRequired),
      KeyNameDescription: row?.keyNameDescription ?? row?.nameKeyDescription ?? row?.KeyNameDescription ?? row?.NameKeyDescription ?? "",
      EnrollmentLeadTime: row?.enrollmentLeadTime ?? row?.EnrollmentLeadTime ?? "",
    });
  }, [reset, row]);

  return (
    <ConfirmationModal
      show={Boolean(row)}
      centered
      dialogClassName="utility-sop-edit-dialog"
      title="Edit State of Operations"
      confirmButtonLabel={saving ? "Saving..." : "Save"}
      isLoading={saving}
      keyboard={!saving}
      onHide={() => { if (!saving) onHide(); }}
      onConfirm={() => {
        if (saving || !getSopEditSnapshot(row)) return;
        const values = getValues();
        const commodityIds = getSopEditSnapshot(row).commodityIds;
        setError("");
        onSave({
          commodityIds,
          serviceReferenceNumberRequired: Boolean(values.ServiceReferenceNumberRequired),
          serviceReferenceNumberDescription: values.ServiceReferenceNumberDescription || null,
          keyNameRequired: Boolean(values.KeyNameRequired),
          keyNameDescription: values.KeyNameDescription ?? "",
          enrollmentLeadTime: values.EnrollmentLeadTime ?? "",
        });
      }}
    >
      <div className="utility-sop-edit-fields">
        <label htmlFor="utility-sop-edit-state">State</label>
        <Select inputId="utility-sop-edit-state" aria-label="State" isDisabled
          value={{ value: "state", label: stateLabel }} options={[]}
          getOptionValue={(option) => option.value} getOptionLabel={(option) => option.label} />
        <Check
          id="utility-edit-name-key-required"
          name="KeyNameRequired"
          control={control}
          label="Name Key Required"
          disabled={saving || !getSopEditSnapshot(row)}
        />
        <FormInput type="text" name="KeyNameDescription" control={control} label="Name Key Description" disabled={saving || !getSopEditSnapshot(row)} />
        <Check
          id="utility-edit-service-reference-required"
          name="ServiceReferenceNumberRequired"
          control={control}
          label="Service Reference Number Required"
          disabled={saving || !getSopEditSnapshot(row)}
        />
        <FormInput type="text" name="ServiceReferenceNumberDescription" control={control} label="Service Reference Number Description" disabled={saving || !getSopEditSnapshot(row)} />
        <FormInput
          type="number"
          name="EnrollmentLeadTime"
          control={control}
          label="Enrollment Lead Time days"
          min={0}
          step={1}
          disabled={saving || !getSopEditSnapshot(row)}
        />
        {error && <p role="alert">{error}</p>}
        {canRefresh && <div>
          <p>Refresh to load the latest mappings and review your selections before retrying.</p>
          <LoadingButton variant="secondary" disabled={saving} onClick={onRefresh}>Refresh</LoadingButton>
        </div>}
      </div>
    </ConfirmationModal>
  );
};

export default EditStateOperationModal;
